// ABOUTME: The reel's top-left concertina rail — bag / saved / privacy, expanding in place (SPEC-26 R.2).
// ABOUTME: "Consetina" in the Figma layer names is concertina: headers accordion open, displacing those below.

import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
// SPEC-53: the page scroller and chevron moved to panelScroll.js so the collection piece card
// scrolls by exactly this code path; behaviour here is unchanged.
import { scrollGridByPage, ScrollHint } from './panelScroll';
import {
  RAIL_UNIT, u, RAIL_LEFT_PCT, RAIL_TOP_PCT, topOffset,
  CHIP, STACK_GAP, PANEL_W, PANEL_PAD, PANEL_CONTENT_W, PANEL_RADIUS, PANEL, GLYPHS, TEXT,
  TILE_COMPACT_SCALE, ITEMS_PER_ROW, ROWS_BEFORE_SCROLL, ITEM_BLOCK_H, ACTION_ICON, CONSENT_PANEL,
  uMin, CHIP_BADGE, panelWidth, panelWidthClearOfCentre, panelWidthClearOfWordmark, panelContentWidth, panelMaxHeight, railUnitFor, NAME_LINES, NAME_LINE_HEIGHT, NAME_BLOCK_H,
} from './railGeometry';
import { itemQuantity } from '../../utils/cartMath';
import ChipBadge from './ChipBadge';
import ConsentPanel from './ConsentPanel';
import RowActions from './RowActions';
import { useViewportSize } from '../Projectionist/useViewportSize';

// Client feedback (2026-08-24): the old two-tile-plus-"N more items"-plus-"See full …" drawer asked
// the guest (or the agent) to open the same list twice. Every item now renders in place — a 2-up
// grid that steps its tile size down past ITEMS_PER_ROW items, and scrolls once it grows past
// ROWS_BEFORE_SCROLL rows instead of shrinking indefinitely.

/**
 * A collapsed 88x88 accordion header: bare glyph on the near-invisible `iconChip` disc.
 *
 * The accessible name stays the same whether the section is open or shut, with the state carried
 * by `aria-expanded` — the standard accordion-header contract, and it keeps one stable selector
 * for the control across both states.
 */
function RailHeader({ glyph, label, onClick, expanded = false, children }) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-expanded={expanded}
      onClick={onClick}
      className="reel-touch-target relative flex items-center justify-center bg-reel-chrome-iconChip backdrop-blur-reel-panelDense rounded-full hover:bg-white/10 transition-colors shrink-0"
      // Touch hit box (index.css, coarse pointers only): 44px wide, and capped at the stack's own
      // pitch vertically so two chips' boxes meet instead of overlapping. A tablet gets THIS
      // composition (SPEC-26 Phase 5) and the chip is 37.8px on it; a mouse sees no change.
      style={{ width: u(CHIP), height: u(CHIP), '--hit-h': `calc(100% + ${u(STACK_GAP)})` }}
    >
      <img
        src={glyph.src}
        alt=""
        aria-hidden="true"
        style={{ width: u(glyph.w), height: u(glyph.h) }}
      />
      {children}
    </button>
  );
}

/**
 * One product tile in the grid — image, 2-line name, price, action strip. Nodes 45:2326-45:2331.
 *
 * `tileSize` scales the whole block (image + the gaps around the text) down together once the grid
 * passes ITEMS_PER_ROW items; only the two font sizes stay fixed at every step; a smaller thumbnail
 * with full-size type reads as "denser", a smaller thumbnail with smaller type just reads as blurry.
 */
function ProductTile({
  item, priceDisplay, linePriceDisplay, onSelect, onToggleSaved, onRemove,
  onIncrement, onDecrement, saved = false, removable = false, tileSize = PANEL.tile, maxSide,
}) {
  const scale = tileSize / PANEL.tile;
  const quantity = itemQuantity(item);
  return (
    // Full-width, not `u(tileSize)`, since 2026-09-01: the tile now fills a half-panel grid column
    // (see RailPanel's `gridTemplateColumns`) instead of being a fixed-width island with dead space
    // to its right. `min-w-0` keeps it able to shrink inside that column.
    <div className="w-full min-w-0">
      {/* The artwork box stays EXACTLY `tileSize` square and centres itself in the wider column
          (`mx-auto`). The client turned down the option of growing the watches to fill the new
          width: a taller row means fewer items before the grid starts scrolling. Pinning the width
          here rather than letting `object-contain` sort it out also keeps the ×N pill — positioned
          against this button — on the corner of the artwork instead of drifting to the far edge of
          the column. */}
      <button
        type="button"
        onClick={() => onSelect?.(item)}
        className="relative block mx-auto"
        // `maxSide` (a panel narrowed to clear the centre chrome): the artwork yields to a column narrower than its authored size.
        style={maxSide ? { width: `min(${u(tileSize)}, ${maxSide})`, height: `min(${u(tileSize)}, ${maxSide})` } : { width: u(tileSize), height: u(tileSize) }}
      >
        <img
          src={item.image_url || item.image_uri}
          alt={item.title || 'Product'}
          className="w-full h-full object-contain"
        />
        {/* ×N pill for a multi-quantity line — ChipBadge treatment, on the artwork's corner.
            aria-hidden like ChipBadge; the count is carried accessibly by the ✕ button label
            and by the line price being the quantity total. */}
        {quantity > 1 && (
          <span
            aria-hidden="true"
            className="absolute top-1 right-1 rounded-full bg-reel-chrome-textPrimary text-reel-chrome-panel font-bold flex items-center justify-center"
            style={{
              minWidth: uMin(CHIP_BADGE.size, CHIP_BADGE.minSize),
              height: uMin(CHIP_BADGE.size, CHIP_BADGE.minSize),
              paddingLeft: u(CHIP_BADGE.padX),
              paddingRight: u(CHIP_BADGE.padX),
              fontSize: uMin(CHIP_BADGE.font, CHIP_BADGE.minFont),
            }}
          >
            ×{quantity}
          </span>
        )}
      </button>
      <div style={{ marginTop: u((PANEL.nameTop - PANEL.tile) * scale) }}>
        {/* Client feedback (2026-09-01), two parts, both about the same row of tiles reading as
            misaligned:
            (a) the name column was authored 265 wide inside a 432 tile (node 45:2329), leaving 167
                authored px of empty tile beside every title and wrapping names far earlier than
                the space required. It ran the tile's full width from that day, and since the
                second round of the same feedback it runs the full COLUMN — `maxWidth: '100%'`
                against a half-panel `1fr` column, ~458 authored px rather than 345.6 in compact
                mode. Expressed as a percentage on purpose: the column width is the browser's to
                decide (see the grid's `1fr` note), and hand-computing it here is exactly how the
                two would drift apart.
            (b) a wrapped title pushed its own price and action strip a line lower than the
                neighbouring tile's, because each tile is an independent grid item. The block below
                reserves exactly NAME_LINES lines whatever the title does: an explicit line-height
                (never the font's default, which varies), a min-height of two of those lines, and a
                2-line clamp. One and two-line titles therefore share a baseline for everything
                under them.
            The clamp is a HARD limit, not a safety net that cannot be reached: `-webkit-line-clamp`
            truncates the third line and renders a visible ellipsis. Measured headroom in the
            4-item compact case (visual pass, 2026-09-01): the tightest of the client's four titles,
            "411 Vesper Turquoise", ends ~26 authored px short of the tile edge — roughly two more
            characters before it takes a third line and gets its tail ellipsised. That is accepted
            deliberately rather than raised: NAME_LINES feeds the fixed row height that ITEM_BLOCK_H
            and `visibleGridH` are derived from, and the whole scroll-paging model (one page = N
            rows of a known height) depends on a tile row that cannot grow. A longer catalogue means
            revisiting the tile, not loosening the clamp.
            `leading-tight` is gone because the number has to be one this file can multiply, not a
            Tailwind class the geometry can't see. */}
        <p
          className="m-0 text-reel-chrome-textPrimary font-reel-heading font-bold"
          style={{
            fontSize: u(TEXT.productLine),
            maxWidth: '100%',
            lineHeight: NAME_LINE_HEIGHT,
            minHeight: u(NAME_BLOCK_H),
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: NAME_LINES,
            overflow: 'hidden',
          }}
        >
          {item.title || 'Untitled'}
        </p>
        <p
          className="m-0 text-reel-chrome-textPrimary font-reel-body"
          style={{ fontSize: u(TEXT.price), marginTop: u((PANEL.priceTop - 72) * scale) }}
        >
          {(linePriceDisplay || priceDisplay)(item)}
        </p>
        {/* Bag rows carry the full strip (heart · − · + · ✕); favourites rows carry the heart
            alone, where it still means "unsave". Which one is decided by the panel KIND via
            `removable`, not by whether a handler happens to be wired — a missing prop must never
            silently change what a row's controls do. */}
        <RowActions
          item={item}
          quantity={quantity}
          saved={removable ? saved : true}
          showQuantity={removable}
          showRemove={removable}
          unit={u}
          heartW={PANEL.heartW * scale}
          heartH={PANEL.heartH * scale}
          iconSize={ACTION_ICON * scale}
          gap={PANEL.actionGap * scale}
          onToggleSaved={onToggleSaved}
          onIncrement={onIncrement}
          onDecrement={onDecrement}
          onRemove={onRemove}
          style={{ marginTop: u((PANEL.heartTop - PANEL.priceTop - TEXT.price) * scale) }}
        />
      </div>
    </div>
  );
}

function Hairline({ width = u(PANEL_CONTENT_W) }) {
  return (
    <div
      className="bg-reel-chrome-textPrimary/30"
      style={{ width, height: u(PANEL.ruleH) }}
    />
  );
}

/** A full-width or centred frosted CTA pill — nodes 45:2339 / 45:2346. */
function PanelButton({ children, onClick, width, widthCss }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="reel-touch-target relative bg-reel-cta-secondaryBg backdrop-blur-reel-panelButton hover:bg-white/15 transition-colors text-reel-chrome-textPrimary font-reel-body flex items-center justify-center"
      style={{
        width: widthCss || u(width),
        height: u(PANEL.ctaH),
        borderRadius: u(PANEL_RADIUS),
        fontSize: u(TEXT.button),
      }}
    >
      {children}
    </button>
  );
}

/**
 * The expanded panel — node 45:2318 / 45:2319. Chosen over the alternate authoring clipped inside
 * `Cart Consetina v4` (45:4839, "Total"/"Checkout", 470px tiles): this is the only version that
 * actually renders on a frame, and the one the client compared the build against. See SPEC-26 OQ-R1.
 *
 * The subtotal + checkout footer is cart-only. The Figma favourites frame (45:2395) shows it too,
 * but that is the mock content copy-pasted from the bag frame — a favourites list has nothing to
 * total. Same class of mock-content artifact as the price mismatch noted in DESIGN.md §4.
 */
function RailPanel({
  kind, glyph, title, label, items, priceDisplay, linePriceDisplay, totalLabel, totalValue,
  onCollapse, onCheckout,
  onSelectItem, onToggleSaved,
  onRemoveItem, unitCount, scrollCommand, onIncrementItem, onDecrementItem, savedIds,
  onScrollStateChange, widthCss,
}) {
  const isCart = kind === 'CART';
  // `widthCss` is set only for a panel narrowed to clear the centre chrome (right rail); everything fixed to the authored content
  // column then follows the panel instead. Unset (the left rail), `contentW` is the authored column and no style changes.
  const contentW = panelContentWidth(widthCss);

  const compact = items.length > ITEMS_PER_ROW;
  const scale = compact ? TILE_COMPACT_SCALE : 1;
  const tileSize = PANEL.tile * scale;
  const rows = Math.ceil(items.length / ITEMS_PER_ROW);
  const scrollable = rows > ROWS_BEFORE_SCROLL;
  const rowGap = PANEL.tileGap * scale;
  const itemH = ITEM_BLOCK_H * scale;
  const visibleGridH = ROWS_BEFORE_SCROLL * itemH + (ROWS_BEFORE_SCROLL - 1) * rowGap;

  const gridRef = useRef(null);
  const prevCountRef = useRef(items.length);
  const upHintRef = useRef(null);
  const downHintRef = useRef(null);
  // Which chevron the guest last activated, so focus can follow it if the scroll it caused makes it
  // disappear — see the focus-rescue effect below.
  const lastHintRef = useRef(null);
  const [scrollHint, setScrollHint] = useState({ up: false, down: false });

  // One read of the grid's scroll position, feeding two consumers: the chevrons (local state) and
  // the agent (`onScrollStateChange`, 2026-09-01). Deliberately the same function for both, because
  // a chevron saying "there is more below" while the agent believes the list is at its end is
  // exactly the inconsistency the upstream report exists to remove.
  //
  // Called from `onScroll` too, so it runs once per frame during a drag — the reporter dedupes,
  // this side must stay cheap. That is also why the agent-driven scroll and the chevron click do
  // not report explicitly: both scroll the element, the element fires `scroll`, and this runs.
  const readScrollState = () => {
    const el = gridRef.current;
    // `overflowing` is measured, not inferred from the `scrollable` layout flag: the flag is a row
    // count, whereas the wire contract's `scrollable` means "is there anywhere to scroll to".
    const overflowing = !!el && el.scrollHeight > el.clientHeight + 4;
    const up = overflowing && el.scrollTop > 4;
    const down = overflowing && el.scrollTop + el.clientHeight < el.scrollHeight - 4;
    if (el) setScrollHint({ up, down });
    onScrollStateChange?.({ panel: kind, scrollable: overflowing, atTop: !up, atBottom: !down });
  };

  // `items.length` is in the deps so an add/remove re-reports: a bag that just dropped to two rows
  // is no longer scrollable, and the agent has to hear that as well as the chevrons.
  useEffect(() => { readScrollState(); }, [items.length, scrollable]);

  // Tell the reporter to forget this panel when it goes away (review, 2026-09-01). The backend pops
  // its own scroll entry the moment App.js sends `closed <panel> panel`, so a cache that outlives
  // the panel leaves the two disagreeing: a remounted grid starts at the top, which is usually the
  // exact state last reported, the dedupe swallows the re-report, and "scroll down" on the reopened
  // panel hits an unknown state and gets answered as if it had worked. Per panel, not a global
  // reset: a CART→WISHLIST switch is a close plus an open, and the open must keep its fresh entry.
  // Separate effect with an empty dep list so it fires on unmount only, never on an items change.
  useEffect(() => () => onScrollStateChange?.({ panel: kind, forget: true }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);

  // Newly added items land off the bottom of a scrolled grid — surface them the same way opening
  // the panel does, without the guest (or the agent) having to scroll manually to see what just
  // changed.
  useEffect(() => {
    const el = gridRef.current;
    if (el && items.length > prevCountRef.current) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
    prevCountRef.current = items.length;
  }, [items.length]);

  // The agent's `scroll_panel` tool — the guest asking "scroll down" while this panel is already
  // open. Scoped to `kind` because both the cart and wishlist panel mount this component and
  // App.js broadcasts one `scrollCommand` regardless of which is open; `nonce` makes repeats (the
  // same direction fired twice) re-trigger the effect even though the object would otherwise look
  // unchanged to React.
  useEffect(() => {
    if (!scrollCommand || scrollCommand.panel !== kind) return;
    scrollGridByPage(gridRef.current, scrollCommand.direction, visibleGridH);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollCommand?.nonce]);

  // The guest's own path to the same action (2026-09-01). Named and hoisted out of the JSX rather
  // than inlined on the chevron, and routed through the same `scrollGridByPage` the agent effect
  // above uses, so "one page" can never come to mean two different distances.
  const handleHintClick = (direction) => {
    lastHintRef.current = direction;
    scrollGridByPage(gridRef.current, direction, visibleGridH);
  };

  // Keyboard rescue (review, 2026-09-01). The chevrons render only on an edge that has more content
  // — deliberately — so activating the last one takes the guest to the end of the list and unmounts
  // the very button they were focused on, dropping focus to <body> and stranding a keyboard user at
  // the top of the document. Hand focus to the surviving sibling instead, which is where the guest
  // wants to be anyway: at the end of a list, the only remaining move is back.
  // Guarded on focus having actually been lost, so a guest who moved on with the keyboard or the
  // mouse before the smooth scroll settled never gets it yanked back.
  useEffect(() => {
    const last = lastHintRef.current;
    if (!last) return;
    if (last === 'up' ? scrollHint.up : scrollHint.down) return; // still there — nothing to rescue
    lastHintRef.current = null;
    if (typeof document === 'undefined' || document.activeElement !== document.body) return;
    (last === 'up' ? downHintRef : upHintRef).current?.focus();
  }, [scrollHint.up, scrollHint.down]);

  return (
    <motion.div
      key={kind}
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
      className="overflow-hidden shrink-0"
      style={{ width: widthCss || panelWidth() }}
    >
      {/* `custom-scrollbar` + `overflow-x-hidden` (reported 2026-09-01: the bag panel showed BOTH a
          vertical and a horizontal system scrollbar; the favourites panel showed neither).
          Two separate causes, one line:
          - Only the bag overflows this container, because only the bag carries the Subtotal +
            "Continue to Checkout" footer under the grid — hence "why just the cart".
          - The horizontal bar was never real content: CSS computes `overflow-x: visible` to `auto`
            as soon as the other axis is not `visible`, so `overflow-y-auto` alone silently opted
            this box into a horizontal scrollbar too.
          The inner grid already hid its own bars with `custom-scrollbar`; this container was simply
          missed. The chevrons are unaffected — they are absolutely positioned -10px outside the
          GRID, which sits inside PANEL_PAD (53.333 authored px) of padding on every side and well
          below the glyph/title/rule stack, so nothing of theirs reaches this box's padding edge; and
          `overflow-x-hidden` clips horizontally only, while they overhang vertically and are
          horizontally centred at 28px wide inside a 930-wide content column.

          It stays `overflow-y-auto` rather than becoming `overflow-hidden` purely as a last-resort
          valve. It is no longer expected to scroll at all: the GRID absorbs the deficit instead (see
          `min-h-0` on the grid wrapper below), which is what keeps the checkout CTA on screen. But a
          viewport short enough that even a zero-height grid does not fit must degrade to a scroll,
          not to silently clipped content. */}
      <div
        className="bg-reel-chrome-panel backdrop-blur-reel-chrome flex flex-col overflow-y-auto overflow-x-hidden custom-scrollbar relative"
        style={{ padding: u(PANEL_PAD), borderRadius: u(PANEL_RADIUS), maxHeight: panelMaxHeight() }}
      >
        {/* The expanded panel absorbs its own header — the glyph moves inside it (node 45:2320)
            and the 88px chip disappears. It stays the accordion's toggle, so clicking it collapses
            the section again; without that there is no way back out of an open panel. */}
        <button
          type="button"
          aria-label={label}
          aria-expanded="true"
          onClick={onCollapse}
          className="reel-touch-target relative block hover:opacity-70 transition-opacity"
        >
          <img
            src={glyph.src}
            alt=""
            aria-hidden="true"
            style={{ width: u(glyph.w), height: u(glyph.h) }}
          />
        </button>

        {/* Explicit close affordance (client Figma frame: "Shopping Bag (4)" with an X on the SAME
            row, baseline with the title — not floating over the glyph/actions). The glyph above
            still re-collapses on a second tap; this shares its exact handler, so the two paths are
            indistinguishable to the `overlay` state effect in App.js that tells the agent a panel
            closed. */}
        <div
          className="flex items-center justify-between"
          style={{ marginTop: u(PANEL.titleTop - PANEL.glyphTop - glyph.h), width: contentW }}
        >
          <h2 className="m-0 text-reel-chrome-textPrimary font-reel-body" style={{ fontSize: u(TEXT.panelTitle) }}>
            {/* Unit count, not row count: two lines of two each is a bag of four. */}
            {`${title} (${unitCount ?? items.length})`}
          </h2>
          <button
            type="button"
            aria-label={`Close ${title}`}
            onClick={onCollapse}
            className="reel-touch-target relative shrink-0 hover:opacity-70 transition-opacity text-reel-chrome-textPrimary"
            style={{ width: u(ACTION_ICON), height: u(ACTION_ICON) }}
          >
            <X aria-hidden="true" style={{ width: u(ACTION_ICON), height: u(ACTION_ICON) }} />
          </button>
        </div>

        <div style={{ marginTop: u(PANEL.ruleTop - PANEL.titleTop - TEXT.panelTitle) }}>
          <Hairline width={contentW} />
        </div>

        {items.length === 0 ? (
          <p
            className="m-0 text-reel-chrome-textDim font-reel-body"
            style={{ fontSize: u(TEXT.panelTitle), marginTop: u(PANEL.gridTop - PANEL.ruleTop) }}
          >
            {isCart ? 'Your shopping bag is empty.' : 'No items saved yet.'}
          </p>
        ) : (
          <>
            {/* `flex min-h-0` on the wrapper and `overflow-y-auto` on the grid are what make the
                GRID the part that gives way when the panel runs out of room (reported 2026-09-01,
                confirmed in pixels on the untouched `desktop-bag-4` plate: at four items the panel
                exceeded `panelMaxHeight()` and pushed "Continue to Checkout" off the bottom — the
                system scrollbar had been the only thing signalling it, and Task 1 removed that).
                A flex item's automatic minimum size is its content, so every child of the panel
                refused to shrink and the overflow went to the container. `min-height: 0` opts this
                one out of that floor, and because the grid inside it carries `overflow-y: auto` its
                own automatic minimum is zero too, so the deficit lands on the scrollable list and
                nowhere else. Nothing is hand-computed: the browser subtracts the header and the
                checkout footer for us, which is why this survives a change to either.
                `overflow-y-auto custom-scrollbar` is now unconditional rather than gated on
                `scrollable` — a shrunk grid overflows at four items, well below the row count that
                flag describes, and without it those rows would simply be cut off. */}
            <div
              className="relative flex flex-col min-h-0"
              style={{ marginTop: u(PANEL.gridTop - PANEL.ruleTop) }}
            >
              <div
                ref={gridRef}
                onScroll={readScrollState}
                className="overflow-y-auto custom-scrollbar"
                style={{
                  display: 'grid',
                  // Two EQUAL half-panel columns (client feedback + redline, 2026-09-01, second
                  // round). The columns used to be fixed at the tile size, which left the row
                  // hugging the panel's left edge: in compact mode 2 x 345.6 + 13.333 = 704.5 of
                  // the 930 content column, i.e. ~225 authored px of dead gutter down the right of
                  // every row, which is what the client redlined. `1fr` hands the split to the
                  // browser instead of hand-computing `(930 - gap) / 2` here — the grid is already
                  // stretched to the content width by the flex column above, and on a narrow
                  // viewport `panelWidth()`'s `min()` cap shrinks that, which a computed constant
                  // would not follow. The COLUMN gets wider; the artwork does not (see ProductTile).
                  gridTemplateColumns: `repeat(${ITEMS_PER_ROW}, 1fr)`,
                  columnGap: u(rowGap),
                  rowGap: u(rowGap),
                  ...(scrollable ? { maxHeight: u(visibleGridH) } : {}),
                }}
              >
                {items.map((item, idx) => (
                  <ProductTile
                    key={item.id || item.sku || idx}
                    item={item}
                    tileSize={tileSize}
                    priceDisplay={priceDisplay}
                    linePriceDisplay={linePriceDisplay}
                    onSelect={onSelectItem}
                    onToggleSaved={onToggleSaved}
                    onRemove={onRemoveItem}
                    onIncrement={onIncrementItem}
                    onDecrement={onDecrementItem}
                    saved={!!savedIds?.has(item.id)}
                    removable={isCart}
                    maxSide={widthCss && `calc((${contentW} - ${u(rowGap)}) / 2)`}
                  />
                ))}
              </div>
              {/* Gated on the MEASURED overflow alone, not on the `scrollable` row-count flag: the
                  grid is a shrinkable flex item now, so it can overflow at four items — below the
                  row count that flag describes — and a chevron that only appeared past four rows
                  would leave those guests no way to reach the rest of the list. */}
              {scrollHint.up && (
                <ScrollHint ref={upHintRef} direction="up" onClick={() => handleHintClick('up')} />
              )}
              {scrollHint.down && (
                <ScrollHint ref={downHintRef} direction="down" onClick={() => handleHintClick('down')} />
              )}
            </div>
          </>
        )}

        {isCart && items.length > 0 && (
          <>
            <div style={{ marginTop: u(PANEL.sectionGap) }}>
              <Hairline width={contentW} />
            </div>
            <div
              className="flex justify-between items-baseline text-reel-chrome-textPrimary font-reel-heading font-bold"
              style={{
                width: contentW,
                fontSize: u(TEXT.panelTitle),
                marginTop: u(PANEL.subtotalGap),
              }}
            >
              <span>{totalLabel}</span>
              <span>{totalValue}</span>
            </div>
            <div style={{ marginTop: u(PANEL.ctaGap) }}>
              {/* SPEC-46 Rev 7 (D-23): one button again. It always renders and always sends
                  checkout_request — the overlay mounts once the offer lands, so there is no
                  second "open" step to branch on. Where this deployment has no publishable key,
                  `onCheckout` (useCheckout's `requestCheckout`) is a silent no-op (JC AC-30):
                  the gap is invisible to the guest rather than removing the CTA. */}
              <PanelButton width={PANEL_CONTENT_W} widthCss={widthCss && contentW} onClick={onCheckout}>
                Continue to Checkout
              </PanelButton>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}

/**
 * The rail itself. `expanded` is App.js's existing `overlay` state, which is why only one panel can
 * ever be open (it is a single slot) and why the agent can drive it for free — `show me my cart`
 * already sets `overlay` to 'CART' in the signalling handler.
 *
 * During the intro film the shopping affordances unmount, exactly as the legacy header does; only
 * the privacy control stays, mirroring PrivacyLauncher's VER-40 behaviour.
 */
function IconRail({
  expanded, onToggle, cartItems, wishlistItems, priceDisplay, linePriceDisplay, cartTotalDisplay,
  cartUnitCount, onRemoveCartLine, onIncrementCartLine, onDecrementCartLine,
  onCheckout, onSelectItem,
  onToggleSaved, privacyLabel,
  consent, onSaveConsent, onOpenPrivacyPolicy,
  recording = false, showIntro = false, scrollCommand = null, onScrollStateChange,
  side = 'left', hidePrivacyChip = false, // SPEC-82: bag_top_right / collection_menu (defaults = today's rail)
}) {
  const cartOpen = expanded === 'CART';
  const wishlistOpen = expanded === 'WISHLIST';
  const privacyOpen = expanded === 'PRIVACY';

  // The privacy panel's content is fixed (three categories, never data-driven like the cart/wishlist
  // grids), so it measures itself against the CC bar and shrinks to fit rather than falling back to a
  // scroll — see ConsentPanel.js. This is just the normal, un-shrunk starting unit for that
  // measurement. Read unconditionally (not just while `privacyOpen`) so opening the panel never has
  // to wait a render for the viewport to be measured.
  const { width: viewportWidth } = useViewportSize();
  const railUnitPx = railUnitFor(viewportWidth);
  // Both rails' panels yield to the centred chrome (owner 2026-10-07): the right rail's to the wordmark + indicator, the left rail's
  // (the shipped default) to the wordmark alone, since the indicator sits in the opposite corner there.
  const panelWidthCss = side === 'right' ? panelWidthClearOfCentre() : panelWidthClearOfWordmark();

  // Which bag rows are also saved, so their heart renders filled. Derived from `wishlistItems`
  // rather than from a flag on the cart line: the wishlist is the single source of truth, and it
  // moves under the agent's tools too — so an agent `add_to_wishlist` on a piece already in the bag
  // fills that bag row's heart with no extra plumbing.
  const savedIds = React.useMemo(
    () => new Set((wishlistItems || []).map(i => i.id)),
    [wishlistItems]
  );

  return (
    <div
      className={`absolute pointer-events-auto flex flex-col ${side === 'right' ? 'items-end' : 'items-start'}`}
      style={{
        // `pointerEvents: 'auto'` is set inline, not just via the Tailwind class above, because it
        // now sits inside an ancestor with an INLINE `pointer-events: none` (App.js portals this rail
        // above the tag/footer layer once the intro ends — see OverlayPortal.js). A Tailwind utility
        // class never resolves through jsdom's `getComputedStyle` (no real stylesheet is loaded in
        // tests), so `@testing-library/user-event`'s pointer-events walk skipped straight past this
        // div and hit the portal's inline `none` instead, false-flagging every rail button as
        // unclickable (reported via a failing test, 2026-08-28) despite working in every real
        // browser, where the Tailwind class DOES apply. Setting it inline too costs nothing and
        // makes the reset verifiable in both.
        pointerEvents: 'auto',
        '--u': RAIL_UNIT,
        ...(side === 'right' ? { right: `${RAIL_LEFT_PCT}%` } : { left: `${RAIL_LEFT_PCT}%` }),
        top: topOffset(RAIL_TOP_PCT),
        gap: u(STACK_GAP),
      }}
    >
      {/* No `mode="popLayout"` and no per-item `layout`: popLayout takes exiting children out of
          flow by absolutely positioning them, which stacked the two collapsed glyphs on top of
          each other underneath an open panel. The flex column already reflows the headers when a
          panel expands between them — that displacement IS the concertina — so only the panel
          itself needs to animate. */}
      <AnimatePresence initial={false}>
        {!showIntro && (cartOpen ? (
          <RailPanel
            key="cart-panel"
            kind="CART"
            glyph={GLYPHS.bag}
            title="Shopping Bag"
            label="Open cart"
            items={cartItems}
            priceDisplay={priceDisplay}
            linePriceDisplay={linePriceDisplay}
            totalLabel="Subtotal"
            totalValue={cartTotalDisplay}
            unitCount={cartUnitCount}
            onCollapse={() => onToggle('CART')}
            onCheckout={onCheckout}
            onSelectItem={onSelectItem}
            onToggleSaved={onToggleSaved}
            onRemoveItem={onRemoveCartLine}
            onIncrementItem={onIncrementCartLine}
            onDecrementItem={onDecrementCartLine}
            savedIds={savedIds}
            scrollCommand={scrollCommand}
            onScrollStateChange={onScrollStateChange}
            widthCss={panelWidthCss}
          />
        ) : (
          // Plain div, not a `motion.div`: this is a sibling of RailPanel inside the same
          // AnimatePresence, and a `motion.div` here with an `exit` variant would make
          // AnimatePresence hold it mounted until that exit animation resolves — which
          // double-renders "Open cart" (this header + the incoming RailPanel) for the whole
          // 280ms cart-open transition, not just the one-off intro reveal. The CSS mount
          // animation (`ui-reveal-fade`) fades it in without an exit hold: it removes exactly
          // when this branch stops rendering, same as before.
          <div key="cart-header" className="shrink-0 ui-reveal-fade">
            <RailHeader
              glyph={GLYPHS.bag}
              label="Open cart"
              onClick={() => onToggle('CART')}
            >
              <ChipBadge count={cartUnitCount ?? cartItems.length} />
            </RailHeader>
          </div>
        ))}

        {!showIntro && (wishlistOpen ? (
          <RailPanel
            key="wishlist-panel"
            kind="WISHLIST"
            glyph={GLYPHS.saved}
            title="Favourites"
            label="Open wishlist"
            items={wishlistItems}
            priceDisplay={priceDisplay}
            onCollapse={() => onToggle('WISHLIST')}
            onSelectItem={onSelectItem}
            onToggleSaved={onToggleSaved}
            scrollCommand={scrollCommand}
            onScrollStateChange={onScrollStateChange}
            widthCss={panelWidthCss}
          />
        ) : (
          <div key="wishlist-header" className="shrink-0 ui-reveal-fade">
            <RailHeader
              glyph={GLYPHS.saved}
              label="Open wishlist"
              onClick={() => onToggle('WISHLIST')}
            >
              <ChipBadge count={wishlistItems.length} />
            </RailHeader>
          </div>
        ))}

        {/* Privacy is the third accordion item, same contract as cart/wishlist (client decision,
            2026-08-24, reversing the 2026-08-12 centred-modal call — see SPEC-26 R.6 OQ-R4
            follow-up). Reads/writes the same `overlay` slot, so it auto-collapses whichever of
            cart/wishlist was open and vice versa, for free. Unlike cart/wishlist it does NOT hide
            during the intro film — VER-40 requires the recording disclosure stay reachable even
            then, which is also why its collapsed header carries the recording indicator below. */}
        {(privacyOpen ? (
          <ConsentPanel
            key="privacy-panel"
            unit={u}
            geometry={CONSENT_PANEL}
            panelW={PANEL_W}
            // The same viewport cap the cart/wishlist panel gets — a bare `u(PANEL_W)` overflowed a
            // desktop-composition phone by 67px (2026-09-03).
            widthCss={panelWidth()}
            panelPad={PANEL_PAD}
            panelRadius={PANEL_RADIUS}
            baseUnitPx={railUnitPx}
            glyph={GLYPHS.privacy}
            consent={consent}
            onSave={(categories) => { onSaveConsent(categories); onToggle('PRIVACY'); }}
            onCollapse={() => onToggle('PRIVACY')}
            onOpenPrivacyPolicy={onOpenPrivacyPolicy}
          />
        ) : (hidePrivacyChip ? null : (
          <div key="privacy-header" className="shrink-0">
            <RailHeader
              glyph={GLYPHS.privacy}
              label={privacyLabel}
              onClick={() => onToggle('PRIVACY')}
            >
              {recording && (
                <span
                  data-testid="recording-indicator"
                  aria-label="Recording active"
                  className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse ring-2 ring-black/40"
                />
              )}
            </RailHeader>
          </div>
        )))}
      </AnimatePresence>
    </div>
  );
}

export default IconRail;
export { RailHeader, RailPanel, ProductTile };
